//@ts-nocheck
import React, { useEffect, useRef, useState, useLocation } from "react";
import {
  LeftOutlined,
  CloseOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Button, Input, Select, DatePicker, message } from "antd";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createStatus,
  createManufacturer,
  createQuotesResponse,
  createQuoteWtb,
  deleteStatus,
  deleteManufacturer,
  deleteQuotesResponse,
  getManufacturers,
  getQuotesResponses,
  getQuotesResponsesNextId,
  getQuotesResponsesByQuoteId,
  getStatuses,
  updateStatus,
  updateManufacturer,
  updateQuotesResponse,
  bulkImportQuoteResponses,
  createOffice,
  updateOffice,
  getNextOfficeId,
  type QuotesResponseItem,
} from "../../utils/API";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";
import CommonHeader from "../../Components/commonHeader";
import { normalizeSortRules, sortRulesToQuery, type SortRule } from "../../utils/sortRules";

const RESPONSES_EXPORT_COLUMNS = [
  { label: "Date", key: "date" },
  { label: "Company", key: "company" },
  { label: "Brand", key: "manu" },
  { label: "Model", key: "model" },
  { label: "Options", key: "options" },
  { label: "QTY", key: "qty" },
  { label: "Price", key: "price" },
  { label: "Asking Price", key: "askingPrice" },
  { label: "Seller Type", key: "dealer" },
  { label: "Have PO", key: "havePo" },
  { label: "Status", key: "statusText" },
  { label: "Response Type", key: "responseType" },
  { label: "Sub Brand", key: "subBrand" },
  { label: "Sub Model", key: "subModel" },
  { label: "Name", key: "name" },
  { label: "Email", key: "email" },
  { label: "Email Subject", key: "subject" },
  { label: "WTB RE Email", key: "wtbReEmail" },
  { label: "WTBD RE Email", key: "wtbdReEmail" },
  { label: "RSS Note", key: "rssNote" },
  { label: "Comments", key: "comments" },
];

const RESPONSES_SORT_KEYS = RESPONSES_EXPORT_COLUMNS.map((column) => column.key);

export {};

const STATUS_TYPE_RESPONSE = "response";
const STATUS_TYPE_RESPONSE_SUB_STATUS = "response_ss";

const SIMPLE_STATUS_OPTIONS = [
  "SOLD !",
  "Want Target",
  "$$ Change",
  "Offer",
  "No Interest",
  "Make Bid",
  "Offer Made",
  "Call",
  "Working W/Seller",
  "Bid Submitted",
  "Rental Only",
  "Cole Comments",
  "Closed",
  "Sellers Price",
  "We Purchased",
].map((value) => ({ value, label: value }));

type StatusOption = {
  id?: number;
  value: string;
  label: string;
  type?: string;
};

interface Manufacturer {
  id: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

type OfficeEntry = {
  id: number;
  country: string;
  state: string;
  zip?: string;
  address1?: string;
  address2?: string;
  shipping?: string;
  phone1?: string;
  phone2?: string;
  fax1?: string;
  fax2?: string;
  company?: string;
  name?: string;
  email?: string;
};

type ContactEntry = {
  id: number;
  name: string;
  email: string;
  phone1: string;
  phone2: string;
  mobile: string;
  fax: string;
  position: string;
  notes: string;
};

type ResponseRow = {
  qid?: number;
  localKey: string;
  id: string;
  company: string;
  manu: string;
  model: string;
  options: string;
  email: string;
  country: string;
  state: string;
  city: string;
  officeId: string;
  name: string;
  phone1: string;
  phone2: string;
  fax1: string;
  fax2: string;
  price: string;
  date: string;
  qty: string;
  addedToWTB: boolean;
  addedToWTS: boolean;
  hot: boolean;
  rssNote?: string;
  responseType: "WTB" | "WTS" | "WTB/WTS" | "WIN";
  dealer: string;
  comments: string;
  status?: string;
  priceRank?: string;
  subStatus?: string;
  zip?: string;
  address1?: string;
  address2?: string;
  shipping?: string;
  offices: OfficeEntry[];
  contacts: ContactEntry[];
};

const createBlankRow = (
  model = "",
  quoteId = "",
  initialId = "",
): ResponseRow => ({
  localKey: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  id: initialId,
  company: "",
  manu: "",
  model,
  options: "",
  email: "",
  country: "",
  state: "",
  city: "",
  officeId: "",
  name: "",
  phone1: "",
  phone2: "",
  fax1: "",
  fax2: "",
  price: "",
  date: dayjs().format("MM/DD/YYYY"),
  qty: "1",
  addedToWTB: true,
  addedToWTS: false,
  hot: false,
  responseType: "WTB",
  dealer: "Dealer",
  comments: "",
  status: "",
  priceRank: "0",
  subStatus: "",
  zip: "",
  address1: "",
  address2: "",
  shipping: "",
  offices: [],
  contacts: [],
});

const normalizeOfficeEntry = (office: any, fallbackId = 1): OfficeEntry => ({
  id: Number(office?.id || fallbackId),
  country: String(office?.country || ""),
  state: String(office?.state || ""),
  zip: String(office?.zip || ""),
  address1: String(office?.address1 || ""),
  address2: String(office?.address2 || ""),
  shipping: String(office?.shipping || ""),
  phone1: String(office?.phone1 || ""),
  phone2: String(office?.phone2 || ""),
  fax1: String(office?.fax1 || ""),
  fax2: String(office?.fax2 || ""),
  company: String(office?.company || ""),
  name: String(office?.name || ""),
  email: String(office?.email || ""),
});

const normalizeContactEntry = (contact: any, fallbackId = 1): ContactEntry => ({
  id: Number(contact?.id || fallbackId),
  name: String(contact?.name || ""),
  email: String(contact?.email || ""),
  phone1: String(contact?.phone1 || ""),
  phone2: String(contact?.phone2 || ""),
  mobile: String(contact?.mobile || ""),
  fax: String(contact?.fax || ""),
  position: String(contact?.position || ""),
  notes: String(contact?.notes || ""),
});

const mapApiToRow = (item: QuotesResponseItem): ResponseRow => {
  const listType = String(item.listType || "WTB").toUpperCase();
  const responseType: ResponseRow["responseType"] =
    listType === "WTS" || listType === "WIN" || listType === "WTB/WTS"
      ? (listType as ResponseRow["responseType"])
      : "WTB";
  const dateValue = item.dateQuoted || item.createdDate || "";
  const parsed = dateValue ? dayjs(dateValue) : null;
  const apiOffices = Array.isArray(item.offices)
    ? item.offices
    : Array.isArray(item.responseOffices)
      ? item.responseOffices
      : [];
  const apiContacts = Array.isArray(item.contacts)
    ? item.contacts
    : Array.isArray(item.responseContacts)
      ? item.responseContacts
      : [];
  const offices = apiOffices.length
    ? apiOffices.map((office, index) =>
        normalizeOfficeEntry(
          office,
          index + 1,
        ),
      )
    : [];
  const contacts = apiContacts.length
    ? apiContacts.map((contact, index) =>
        normalizeContactEntry(
          contact,
          index + 1,
        ),
      )
    : [];
  const primaryOffice = offices[0];

  return {
    qid: item.qid,
    localKey: `qid-${item.qid}`,
    id: String(item.id || item.qid || ""),
    company: item.companyName || "",
    manu: item.eqBrand || "",
    model: item.eqModel || "",
    options: item.options || "",
    email: item.email || "",
    country: primaryOffice?.country || "",
    state: primaryOffice?.state || "",
    city: "",
    officeId: String(item.recordId || ""),
    name: primaryOffice?.name || item.dealerQuoted || "",
    phone1: primaryOffice?.phone1 || "",
    phone2: primaryOffice?.phone2 || "",
    fax1: primaryOffice?.fax1 || "",
    fax2: primaryOffice?.fax2 || "",
    price: item.priceString || (item.price != null ? String(item.price) : ""),
    date: parsed && parsed.isValid() ? parsed.format("MM/DD/YYYY") : "",
    qty: "1",
    addedToWTB: responseType.includes("WTB"),
    addedToWTS: responseType.includes("WTS"),
    hot: item.hot === true || (item as any).hot === 1,
    rssNote: (item as any).rssNote || "",
    responseType,
    dealer: String(item.sellerType || "Dealer"),
    comments: item.salesComments || "",
    status: item.dStatus || "",
    priceRank: item.priceRange || "0",
    subStatus: (item as any).dsubstatus || (item as any).subStatus || "",
    zip: primaryOffice?.zip || "",
    address1: primaryOffice?.address1 || "",
    address2: primaryOffice?.address2 || "",
    shipping: primaryOffice?.shipping || "",
    offices,
    contacts,
  };
};

const parsePriceNumber = (value: string) => {
  const parsed = Number(String(value || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const mapRowToPayload = (row: ResponseRow): Partial<QuotesResponseItem> => ({
  id: row.id || undefined,
  companyName: row.company || undefined,
  createdDate: row.date
    ? dayjs(row.date, "MM/DD/YYYY").toISOString()
    : undefined,
  price: parsePriceNumber(row.price),
  priceString: row.price || undefined,
  eqBrand: row.manu || undefined,
  eqModel: row.model || undefined,
  options: row.options || undefined,
  dateQuoted: row.date || undefined,
  dealerQuoted: row.name || undefined,
  salesComments: row.comments || undefined,
  dStatus: row.status || undefined,
  priceRange: row.priceRank || undefined,
  sellerType: row.dealer || "Dealer",
  listType: row.responseType,
  hot: row.hot,
  rssNote: row.rssNote || undefined,
  dsubstatus: row.subStatus || undefined,
  email: row.email || undefined,
  recordId: row.officeId || undefined,
  offices: (row.offices || []).map((office) => ({
    id: office.id,
    country: office.country || "",
    state: office.state || "",
    zip: office.zip || "",
    address1: office.address1 || "",
    address2: office.address2 || "",
    shipping: office.shipping || "",
    phone1: office.phone1 || "",
    phone2: office.phone2 || "",
    fax1: office.fax1 || "",
    fax2: office.fax2 || "",
    company: office.company || "",
    name: office.name || "",
    email: office.email || "",
  })),
  contacts: (row.contacts || []).map((contact) => ({
    id: contact.id,
    name: contact.name || "",
    email: contact.email || "",
    phone1: contact.phone1 || "",
    phone2: contact.phone2 || "",
    mobile: contact.mobile || "",
    fax: contact.fax || "",
    position: contact.position || "",
    notes: contact.notes || "",
  })),
  responseOffices: (row.offices || []).map((office) => office.id).filter(Boolean),
  responseContacts: (row.contacts || []).map((contact) => ({
    id: contact.id,
    name: contact.name || "",
    email: contact.email || "",
    phone1: contact.phone1 || "",
    phone2: contact.phone2 || "",
    mobile: contact.mobile || "",
    fax: contact.fax || "",
    position: contact.position || "",
    notes: contact.notes || "",
  })),
});

// Input field styling
const inputStyle: React.CSSProperties = {
  height: 24,
  fontSize: 12,
  borderRadius: 0,
  width: "100%",
};

const labelStyle = "text-[12px] text-black mb-[2px] font-semibold leading-none";

// Custom Segmented Control Component
const Seg = ({
  a,
  b,
  value,
  onChange,
  disabled,
  trueActiveClass,
  falseActiveClass,
}: {
  a: string;
  b: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  trueActiveClass?: string;
  falseActiveClass?: string;
}) => (
  <div className="flex h-[28px] border border-[#A29F9F] rounded overflow-hidden">
    <button
      className={`seg-btn seg-btn-yes flex-1 px-3 text-xs font-medium ${
        value
          ? `${trueActiveClass || "bg-[#004AAD] text-white"} seg-active`
          : "bg-white text-gray-700 seg-inactive"
      }`}
      type="button"
      disabled={disabled}
      onClick={() => onChange(true)}
    >
      {a}
    </button>
    <button
      className={`seg-btn seg-btn-no flex-1 px-3 text-xs font-medium ${
        !value
          ? `${falseActiveClass || "bg-[#004AAD] text-white"} seg-active`
          : "bg-white text-gray-700 seg-inactive"
      }`}
      type="button"
      disabled={disabled}
      onClick={() => onChange(false)}
    >
      {b}
    </button>
  </div>
);

const RadioGroup = ({
  value,
  onChange,
}: {
  value: ResponseRow["responseType"];
  onChange: (v: ResponseRow["responseType"]) => void;
}) => {
  const opts: Array<ResponseRow["responseType"]> = [
    "WTB",
    "WTS",
    "WTB/WTS",
    "WIN",
  ];
  return (
    <div className="flex flex-wrap gap-4">
      {opts.map((o) => (
        <label key={o} className="flex items-center gap-1 text-[12px] leading-none">
          <input
            type="radio"
            checked={value === o}
            onChange={() => onChange(o)}
            className="text-[#004AAD]"
          />
          <span>{o}</span>
        </label>
      ))}
    </div>
  );
};

// ---- Office(s) / Contact(s) tabbed panel ----
const tabBtnBase: React.CSSProperties = {
  padding: "2px 10px",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid #6f6f6f",
  borderBottom: "none",
  background: "#c7c7c7",
  color: "#000000",
  borderRadius: 0,
  marginRight: 1,
};
const tabBtnActive: React.CSSProperties = { ...tabBtnBase, background: "#dcdcdc", color: "#000000", borderBottom: "1px solid #dcdcdc" };
const panelBorder: React.CSSProperties = { border: "1px solid #4f4f4f", background: "#d0d0d0" };
const tblHeaderStyle: React.CSSProperties = { color: "#000000", fontSize: 11, fontWeight: 700, textAlign: "left", padding: "1px 6px", borderBottom: "1px solid #6f6f6f", background: "#ece8da", lineHeight: "14px" };
const tblCellStyle: React.CSSProperties = { padding: "1px 6px", fontSize: 11, borderBottom: "1px solid #8f8f8f", borderRight: "1px solid #8f8f8f", cursor: "pointer", whiteSpace: "nowrap", lineHeight: "16px" };
const detailLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: "#a000a0", minWidth: 60, textAlign: "right", flexShrink: 0 };
const detailInput: React.CSSProperties = { height: 19, fontSize: 11, borderRadius: 0, border: "1px solid #bdbdbd", padding: "0 5px", background: "#ffffff" };
const miniBtn: React.CSSProperties = { width: 18, height: 16, fontSize: 10, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #6f6f6f", background: "#c7c7c7", cursor: "pointer", fontWeight: 700, color: "#000000", borderRadius: 0 };

const OfficeContactTabs = ({
  row,
  onChange,
  tabRowRightContent,
}: {
  row: ResponseRow;
  onChange: (patch: Partial<ResponseRow>) => void;
  tabRowRightContent?: React.ReactNode;
}) => {
  const [activeTab, setActiveTab] = useState<"offices" | "contacts">("offices");
  const [selectedOfficeIdx, setSelectedOfficeIdx] = useState(0);
  const [selectedContactIdx, setSelectedContactIdx] = useState(0);
  const [nextOfficeId, setNextOfficeId] = useState(1);
  const [nextContactId, setNextContactId] = useState(1);
  const offices = row.offices || [];
  const contacts = row.contacts || [];
  const officeListWidth = activeTab === "contacts" ? 172 : 220;
  const contactListWidth = activeTab === "contacts" ? 180 : 0;
  const detailPanelWidth = activeTab === "contacts" ? 760 : 880;

  const selectedOffice = offices[selectedOfficeIdx];
  const selectedContact = contacts[selectedContactIdx];

  useEffect(() => {
    if (selectedOfficeIdx > Math.max(offices.length - 1, 0)) {
      setSelectedOfficeIdx(0);
    }
  }, [offices.length, selectedOfficeIdx]);

  useEffect(() => {
    if (selectedContactIdx > Math.max(contacts.length - 1, 0)) {
      setSelectedContactIdx(0);
    }
  }, [contacts.length, selectedContactIdx]);

  const getNextSequentialId = (items: Array<{ id: number }>, fallbackStart: number) => {
    if (!items.length) return fallbackStart;
    const lastId = Number(items[items.length - 1]?.id || 0);
    return Number.isFinite(lastId) && lastId > 0 ? lastId + 1 : fallbackStart;
  };

  const updateOffices = (nextOffices: OfficeEntry[], nextIndex = selectedOfficeIdx) => {
    const safeIndex = Math.min(Math.max(nextIndex, 0), Math.max(nextOffices.length - 1, 0));
    const activeOffice = nextOffices[safeIndex];
    const fallbackValues = activeOffice
      ? {
          country: activeOffice.country || "",
          state: activeOffice.state || "",
          zip: activeOffice.zip || "",
          address1: activeOffice.address1 || "",
          address2: activeOffice.address2 || "",
          shipping: activeOffice.shipping || "",
          phone1: activeOffice.phone1 || "",
          phone2: activeOffice.phone2 || "",
          fax1: activeOffice.fax1 || "",
          fax2: activeOffice.fax2 || "",
          company: activeOffice.company || "",
          name: activeOffice.name || "",
        }
      : {
          country: row.country || "",
          state: row.state || "",
          zip: row.zip || "",
          address1: row.address1 || "",
          address2: row.address2 || "",
          shipping: row.shipping || "",
          phone1: row.phone1 || "",
          phone2: row.phone2 || "",
          fax1: row.fax1 || "",
          fax2: row.fax2 || "",
          company: row.company || "",
          name: row.name || "",
        };
    onChange({
      offices: nextOffices,
      officeId: activeOffice ? String(activeOffice.id || "") : "",
      ...fallbackValues,
    });
    setSelectedOfficeIdx(safeIndex);
  };

  const updateContacts = (nextContacts: ContactEntry[], nextIndex = selectedContactIdx) => {
    const safeIndex = Math.min(Math.max(nextIndex, 0), Math.max(nextContacts.length - 1, 0));
    const activeContact = nextContacts[safeIndex];
    onChange({
      contacts: nextContacts,
      name: activeContact?.name || "",
      email: activeContact?.email || "",
      phone1: activeContact?.phone1 || "",
      phone2: activeContact?.phone2 || "",
    });
    setSelectedContactIdx(safeIndex);
  };

  const addOffice = async () => {
    try {
      const { nextId } = await getNextOfficeId();
      const created = await createOffice({ id: nextId, officeName: String(nextId) });
      updateOffices(
        [
          ...offices,
          {
            id: created.id ?? nextId,
            country: "",
            state: "",
            zip: "",
            address1: "",
            address2: "",
            shipping: "",
            phone1: "",
            phone2: "",
            fax1: "",
            fax2: "",
            company: "",
            name: "",
            email: "",
          },
        ],
        offices.length,
      );
      setNextOfficeId((created.id ?? nextId) + 1);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to create office");
    }
  };

  const removeOffice = (idx: number) => {
    updateOffices(offices.filter((_, i) => i !== idx), 0);
  };

  const addContact = () => {
    const nextId = getNextSequentialId(contacts, nextContactId || 1);
    updateContacts(
      [
        ...contacts,
        { id: nextId, name: "", email: "", phone1: "", phone2: "", mobile: "", fax: "", position: "", notes: "" },
      ],
      contacts.length,
    );
    setNextContactId(nextId + 1);
  };

  const removeContact = (idx: number) => {
    updateContacts(contacts.filter((_, i) => i !== idx), 0);
  };

  const updateContact = (patch: Partial<ContactEntry>) => {
    if (selectedContact == null) return;
    updateContacts(
      contacts.map((c, i) => (i === selectedContactIdx ? { ...c, ...patch } : c)),
      selectedContactIdx,
    );
  };

  const updateSelectedOffice = (patch: Partial<OfficeEntry>) => {
    if (!selectedOffice) return;
    const updated = { ...selectedOffice, ...patch };
    updateOffices(
      offices.map((office, idx) => (idx === selectedOfficeIdx ? updated : office)),
      selectedOfficeIdx,
    );
    if (updated.id) {
      updateOffice(updated.id, {
        officeName: updated.name || String(updated.id),
        companyName: updated.company || null,
        country: updated.country || null,
        state: updated.state || null,
        zipCode: updated.zip || null,
        addressLine1: updated.address1 || null,
        addressLine2: updated.address2 || null,
        shippingAddress: updated.shipping || null,
        phone1: updated.phone1 || null,
        phone2: updated.phone2 || null,
        fax1: updated.fax1 || null,
        email: updated.email || null,
      }).catch((err: any) => {
        message.error(err?.response?.data?.message || "Failed to update office");
      });
    }
  };

  return (
    <div>
      {/* Tab buttons row — tab buttons on left, extra content on right */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 0, flexWrap: "wrap" }}>
        <button type="button" style={activeTab === "offices" ? tabBtnActive : tabBtnBase} onClick={() => setActiveTab("offices")}>Office(s)</button>
        <button type="button" style={activeTab === "contacts" ? tabBtnActive : tabBtnBase} onClick={() => setActiveTab("contacts")}>Contact(s)</button>
        {tabRowRightContent && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginLeft: 8, paddingBottom: 0, paddingTop: 1 }}>
            {tabRowRightContent}
          </div>
        )}
      </div>

      {/* Tab content */}
      <div style={{ ...panelBorder, display: "flex", minHeight: 200, width: officeListWidth + contactListWidth + detailPanelWidth }}>
        {/* Left: Offices table */}
        <div style={{ ...panelBorder, width: officeListWidth, overflowY: "auto", maxHeight: 260, flexShrink: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr><th colSpan={4} style={{ ...tblHeaderStyle, fontStyle: "italic" }}>Offices</th></tr>
              <tr>
                <th style={tblHeaderStyle}>ID</th>
                <th style={tblHeaderStyle}>Country</th>
                <th style={tblHeaderStyle}>State</th>
                <th style={{ ...tblHeaderStyle, width: 28, textAlign: "center" }}>
                  {activeTab === "offices" ? (
                    <button type="button" style={miniBtn} onClick={addOffice} title="Add office">+</button>
                  ) : null}
                </th>
              </tr>
            </thead>
            <tbody>
              {offices.map((office, idx) => (
                <tr
                  key={office.id}
                  style={idx === selectedOfficeIdx ? { background: "#EBF2FF", color: "#004AAD" } : {}}
                  onClick={() => updateOffices(offices, idx)}
                >
                  <td style={tblCellStyle}>{office.id}</td>
                  <td style={tblCellStyle}>{office.country}</td>
                  <td style={tblCellStyle}>{office.state}</td>
                  <td style={{ ...tblCellStyle, width: 28, textAlign: "center" }}>
                    <div style={{ display: "flex", gap: 1, justifyContent: "center" }}>
                      {activeTab === "offices" && idx === selectedOfficeIdx && (
                        <button type="button" style={miniBtn} onClick={(e) => { e.stopPropagation(); removeOffice(idx); }} title="Delete">x</button>
                      )}
                      <button type="button" style={miniBtn} onClick={(e) => { e.stopPropagation(); updateOffices(offices, idx); }} title="Select">&gt;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Middle: Contacts table (Contact(s) tab only) */}
        {activeTab === "contacts" && (
          <div style={{ ...panelBorder, width: contactListWidth, overflowY: "auto", maxHeight: 260, flexShrink: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th colSpan={3} style={{ ...tblHeaderStyle, fontStyle: "italic" }}>Contacts</th></tr>
                <tr>
                  <th style={tblHeaderStyle}>ID</th>
                  <th style={tblHeaderStyle}>Name</th>
                  <th style={tblHeaderStyle}><button type="button" style={miniBtn} onClick={addContact} title="Add contact">+</button></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, idx) => (
                  <tr
                    key={contact.id}
                    style={idx === selectedContactIdx ? { background: "#EBF2FF", color: "#004AAD" } : {}}
                    onClick={() => updateContacts(contacts, idx)}
                  >
                    <td style={tblCellStyle}>{contact.id}</td>
                    <td style={tblCellStyle}>{contact.name}</td>
                    <td style={tblCellStyle}>
                      <div style={{ display: "flex", gap: 1 }}>
                        <button type="button" style={miniBtn} onClick={(e) => { e.stopPropagation(); updateContacts(contacts, idx); }} title="Active">A</button>
                        {idx === selectedContactIdx && (
                          <button type="button" style={miniBtn} onClick={(e) => { e.stopPropagation(); removeContact(idx); }} title="Delete">x</button>
                        )}
                        <button type="button" style={miniBtn} onClick={(e) => { e.stopPropagation(); updateContacts(contacts, idx); }} title="View">&gt;</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Right: Detail form */}
        <div style={{ ...panelBorder, width: detailPanelWidth, padding: "8px 12px", overflowY: "auto", flexShrink: 0 }}>
          {activeTab === "offices" ? (
            /* ---- Office Detail ---- */
            selectedOffice ? (
              <div>
                <div style={{ ...detailLabel, textAlign: "left", marginBottom: 6, fontSize: 12 }}>Office: &nbsp;{selectedOffice.id}</div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={detailLabel}>Name:</span>
                  <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedOffice?.name || row.name} onChange={(e) => updateSelectedOffice({ name: e.target.value })} />
                  <span style={{ ...detailLabel, minWidth: 60 }}>Company:</span>
                  <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedOffice?.company || row.company} onChange={(e) => updateSelectedOffice({ company: e.target.value })} />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={detailLabel}>Email:</span>
                  <Input style={{ ...detailInput, flex: 2 }} size="small" value={selectedOffice?.email || ""} onChange={(e) => updateSelectedOffice({ email: e.target.value })} />
                  <button type="button" style={{ ...miniBtn, width: 20, height: 20 }} onClick={() => { if (selectedOffice?.email) window.open(`mailto:${selectedOffice.email}`); }} title="Send email">@</button>
                  <span style={{ ...detailLabel, minWidth: 55 }}>Country:</span>
                  <Input style={{ ...detailInput, width: 70 }} size="small" value={selectedOffice?.country || row.country} onChange={(e) => updateSelectedOffice({ country: e.target.value })} />
                  <span style={{ ...detailLabel, minWidth: 35 }}>State:</span>
                  <Input style={{ ...detailInput, width: 50 }} size="small" value={selectedOffice?.state || row.state} onChange={(e) => updateSelectedOffice({ state: e.target.value })} />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={detailLabel}>Phone 1:</span>
                  <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedOffice?.phone1 || row.phone1} onChange={(e) => updateSelectedOffice({ phone1: e.target.value })} />
                  <span style={{ ...detailLabel, minWidth: 25 }}>PO:</span>
                  <Input style={{ ...detailInput, width: 90 }} size="small" />
                  <span style={{ ...detailLabel, minWidth: 25 }}>Zip:</span>
                  <Input style={{ ...detailInput, width: 70 }} size="small" value={selectedOffice?.zip || row.zip || ""} onChange={(e) => updateSelectedOffice({ zip: e.target.value })} />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={detailLabel}>Phone 2:</span>
                  <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedOffice?.phone2 || row.phone2} onChange={(e) => updateSelectedOffice({ phone2: e.target.value })} />
                  <span style={{ ...detailLabel, minWidth: 65 }}>Address 1:</span>
                  <Input style={{ ...detailInput, flex: 2 }} size="small" value={selectedOffice?.address1 || row.address1 || ""} onChange={(e) => updateSelectedOffice({ address1: e.target.value })} />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={detailLabel}>Fax 1:</span>
                  <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedOffice?.fax1 || row.fax1} onChange={(e) => updateSelectedOffice({ fax1: e.target.value })} />
                  <span style={{ ...detailLabel, minWidth: 65 }}>Address 2:</span>
                  <Input style={{ ...detailInput, flex: 2 }} size="small" value={selectedOffice?.address2 || row.address2 || ""} onChange={(e) => updateSelectedOffice({ address2: e.target.value })} />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={detailLabel}>Fax 2:</span>
                  <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedOffice?.fax2 || row.fax2} onChange={(e) => updateSelectedOffice({ fax2: e.target.value })} />
                  <span style={{ ...detailLabel, minWidth: 65 }}>Shipping:</span>
                  <Input style={{ ...detailInput, flex: 2 }} size="small" value={selectedOffice?.shipping || row.shipping || ""} onChange={(e) => updateSelectedOffice({ shipping: e.target.value })} />
                </div>
              </div>
            ) : <div style={{ fontSize: 11, color: "#666" }}>No office selected</div>
          ) : (
            /* ---- Contact Detail ---- */
            selectedContact ? (
              <div style={{ display: "flex", gap: 18 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ ...detailLabel, textAlign: "left", marginBottom: 6, fontSize: 12 }}>ID: &nbsp;{selectedContact.id}</div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={detailLabel}>Name:</span>
                    <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedContact.name} onChange={(e) => updateContact({ name: e.target.value })} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={detailLabel}>Email:</span>
                    <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedContact.email} onChange={(e) => updateContact({ email: e.target.value })} />
                    <button type="button" style={{ ...miniBtn, width: 20, height: 20 }} onClick={() => { if (selectedContact.email) window.open(`mailto:${selectedContact.email}`); }} title="Send email">@</button>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={detailLabel}>Phone 1:</span>
                    <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedContact.phone1} onChange={(e) => updateContact({ phone1: e.target.value })} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={detailLabel}>Phone 2:</span>
                    <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedContact.phone2} onChange={(e) => updateContact({ phone2: e.target.value })} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={detailLabel}>Mobile:</span>
                    <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedContact.mobile} onChange={(e) => updateContact({ mobile: e.target.value })} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={detailLabel}>Fax:</span>
                    <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedContact.fax} onChange={(e) => updateContact({ fax: e.target.value })} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={detailLabel}>Position:</span>
                    <Input style={{ ...detailInput, flex: 1 }} size="small" value={selectedContact.position} onChange={(e) => updateContact({ position: e.target.value })} />
                  </div>
                </div>

                {/* Notes */}
                <div style={{ width: 160, display: "flex", flexDirection: "column" }}>
                  <span style={{ ...detailLabel, textAlign: "left", marginBottom: 4 }}>Notes:</span>
                  <Input.TextArea
                    style={{ ...detailInput, height: "auto", flex: 1, resize: "none" }}
                    rows={7}
                    value={selectedContact.notes}
                    onChange={(e) => updateContact({ notes: e.target.value })}
                  />
                </div>
              </div>
            ) : <div style={{ fontSize: 11, color: "#666" }}>No contact selected. Click + to add one.</div>
          )}
        </div>

        {/* HOT or Not?! — right side of office panel */}
        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "10px 10px 0 10px", borderLeft: "1px solid #4f4f4f" }}>
          <div style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>HOT or Not?!</div>
          <div style={{ height: 22, width: 90, background: row.hot ? "#ff5757" : "#ffb3b3", border: "1px solid #a29f9f", marginTop: 6 }} />
        </div>
      </div>

      {/* URL bar (Office tab only) */}
      {activeTab === "offices" && (
        <div style={{ ...panelBorder, display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", marginTop: 0, width: officeListWidth + detailPanelWidth }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#6C757D", flexShrink: 0 }}>URL:</span>
          <Input style={{ ...detailInput, flex: 1 }} size="small" />
        </div>
      )}
    </div>
  );
};

const ResponseCard = ({
  row,
  manufacturerOptions,
  statusOptions,
  subStatusOptions,
  onOpenStatusModal,
  onOpenMainStatusModal,
  onOpenManufacturerModal,
  onChange,
  onIdChange,
  onIdLookup,
  onAddWts,
  isFindMode = false,
  findFields = {},
  onFindChange,
  onPerformFind,
}: {
  row: ResponseRow;
  manufacturerOptions: { value: string; label: string }[];
  statusOptions: StatusOption[];
  subStatusOptions: StatusOption[];
  onOpenStatusModal: () => void;
  onOpenMainStatusModal: () => void;
  onOpenManufacturerModal: () => void;
  onChange: (patch: Partial<ResponseRow>) => void;
  onIdChange: (id: string) => void;
  onIdLookup: (id: string) => void;
  onAddWts: () => void;
  isFindMode?: boolean;
  findFields?: Record<string, string>;
  onFindChange?: (field: string, value: string) => void;
  onPerformFind?: () => void;
}) => {
  const fv = findFields as any;
  const fi = (field: string, className = "", style: React.CSSProperties = {}) => (
    <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
      <Input
        className={className}
        style={style}
        value={fv[field] || ""}
        onChange={(e) => onFindChange?.(field, e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onPerformFind?.(); } }}
      />
      <SearchOutlined style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: 11, pointerEvents: "none" }} />
    </div>
  );

  const [hotText, setHotText] = React.useState(row.hot ? "Yes" : "");
  React.useEffect(() => { setHotText(row.hot ? "Yes" : ""); }, [row.hot]);

  const navigate = useNavigate();

  const goBackToQuotes = () => {
    window.location.href = "/Quotes";
  };

  const handleRss = () => {
    const model = row.model?.trim();
    const note = dayjs().format("MM/DD/YYYY HH:mm");
    onChange({ rssNote: note });
    const dest = model ? `/Quotes?model=${encodeURIComponent(model)}` : "/Quotes";
    navigate(dest);
  };
  const handleFindEmail = () => {
    if (!row.email) return;
    window.open(`mailto:${row.email}`);
  };

  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: "#000", display: "block", marginBottom: 4, lineHeight: "12px" };
  const inp: React.CSSProperties = { height: 22, fontSize: 11, borderRadius: 0, width: "100%", padding: "0 8px" };
  const smBtn: React.CSSProperties = { height: 20, border: "1px solid #555", background: "#c7c7c7", fontSize: 11, padding: "0 6px", cursor: "pointer", whiteSpace: "nowrap", borderRadius: 0 };
  const subStatusSelectOptions = [
    ...subStatusOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
    { value: "__edit_response_sub_status__", label: "✎ Edit Sub Status..." },
  ];
  const tabRightContent = (
    <>
      <span style={{ fontSize: 11, fontWeight: 700, marginRight: 2 }}>Sub Status:</span>
      <Select
        value={row.subStatus || undefined}
        onChange={(v) => {
          if (v === "__edit_response_sub_status__") {
            onOpenStatusModal();
            return;
          }
          onChange({ subStatus: String(v || "") });
        }}
        style={{ height: 20, width: 100, fontSize: 11 }} size="small"
        options={subStatusSelectOptions}
      />
      <DatePicker
        value={row.date ? dayjs(row.date, "MM/DD/YYYY") : null}
        format="MM/DD/YYYY"
        allowClear={false}
        onChange={(_, dateString) => onChange({ date: dateString })}
        style={{ height: 22, width: 108, fontSize: 11, borderRadius: 0, marginLeft: 8 }}
        size="small"
      />
      <Input
        value={row.responseType}
        onChange={(e) => onChange({ responseType: e.target.value as any })}
        style={{ height: 22, width: 52, fontSize: 11, fontWeight: 600, borderRadius: 0, padding: "0 6px", marginLeft: 4 }}
        size="small"
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 1, marginLeft: 28, position: "relative", top: -12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#c00000", lineHeight: "11px", marginBottom: 2, position: "relative", top: -6 }}>Response Type</span>
        <div style={{ display: "flex", alignItems: "center", gap: 12, whiteSpace: "nowrap", marginTop: 6 }}>
          {(["WTB", "WTS", "WTBD", "WTSQB", "DWIN"] as const).map((opt) => (
            <label key={opt} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", color: "#c00000" }}>
              <input
                type="radio"
                name={`rt-${row.localKey}`}
                checked={row.responseType === opt}
                onChange={() => onChange({ responseType: opt as any })}
                style={{ accentColor: "#000000" }}
              />
              {opt}
            </label>
          ))}
          <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 3, color: "#c00000", fontWeight: 700, whiteSpace: "nowrap", marginLeft: 18 }}>
            <input type="radio" name={`dealer2-${row.localKey}`} checked={row.dealer === "Dealer"} onChange={() => onChange({ dealer: "Dealer" })} style={{ accentColor: "#000000" }} />
            Dealer
          </label>
          <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", color: "#c00000" }}>
            <input type="radio" name={`dealer2-${row.localKey}`} checked={row.dealer === "EU"} onChange={() => onChange({ dealer: "EU" })} style={{ accentColor: "#000000" }} />
            EU
          </label>
        </div>
      </div>
    </>
  );

  return (
    <div style={{ border: isFindMode ? "3px solid #faad14" : "1px solid #7f7f7f", background: "#dcdcdc", padding: "8px 10px", marginBottom: 8 }} className="qr-response-card">

      {/* ── ROW 1: Company / Manu / Model / Options / Price / Date / GoBack — single tight row ── */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 130, flexShrink: 0 }}>
          <span style={lbl}>Company</span>
          {isFindMode ? fi("company", "", { ...inp, background: "#FF8C00", fontWeight: 600, color: "#00008b" }) : (
            <Input value={row.company} onChange={(e) => onChange({ company: e.target.value })}
              style={{ ...inp, background: "#FF8C00", fontWeight: 600, color: "#00008b" }} size="small" />
          )}
        </div>
        <button type="button" style={{ ...miniBtn, marginBottom: 0, flexShrink: 0 }} disabled={isFindMode}>+</button>
        <div style={{ width: 220, flexShrink: 0 }}>
          <span style={lbl}>Manu</span>
          {isFindMode ? (
            <Select showSearch allowClear value={fv.manu || undefined} onChange={(value) => onFindChange?.("manu", value || "")}
              style={{ ...inp, width: "100%" }} size="small" options={manufacturerOptions} placeholder="Search manu..."
              filterOption={(input, option) => String(option?.label || "").toLowerCase().startsWith(input.toLowerCase())} />
          ) : (
            <Select showSearch value={row.manu || undefined}
              onChange={(value) => {
                if (value === "__edit_manufacturer__") { onOpenManufacturerModal(); return; }
                onChange({ manu: String(value || "") });
              }}
              style={{ ...inp, width: "100%", color: "#003bb3" }} className="qr-blue-select" size="small"
              options={[
                ...manufacturerOptions,
                { value: "separator", label: "──────────", disabled: true },
                { value: "__edit_manufacturer__", label: "✎ Edit Manufacturers..." },
              ]}
              placeholder="Select manufacturer"
              filterOption={(input, option) => option?.value === "__edit_manufacturer__" || option?.disabled === true || String(option?.label || "").toLowerCase().startsWith(input.toLowerCase())} />
          )}
        </div>
        <div style={{ width: 80, flexShrink: 0 }}>
          <span style={lbl}>Model</span>
          {isFindMode ? fi("model", "", { ...inp, background: "#FFFF66", color: "#003bb3", fontWeight: 600 }) : (
            <Input value={row.model} onChange={(e) => onChange({ model: e.target.value })}
              style={{ ...inp, background: "#FFFF66", color: "#003bb3", fontWeight: 600 }} size="small" />
          )}
        </div>
        <div style={{ width: 170, flexShrink: 0 }}>
          <span style={lbl}>Options</span>
          {isFindMode ? fi("options", "", { ...inp, color: "#003bb3" }) : (
            <Input value={row.options} onChange={(e) => onChange({ options: e.target.value })}
              style={{ ...inp, color: "#003bb3" }} size="small" />
          )}
        </div>
        <div style={{ width: 80, flexShrink: 0 }}>
          <span style={lbl}>Price</span>
          {isFindMode ? fi("price", "", { ...inp, color: "#003bb3", fontWeight: 600 }) : (
            <Input value={row.price} onChange={(e) => onChange({ price: e.target.value })}
              style={{ ...inp, color: "#003bb3", fontWeight: 600 }} size="small" />
          )}
        </div>
        <div style={{ width: 100, flexShrink: 0 }}>
          <span style={lbl}>Date</span>
          <DatePicker
            value={
              isFindMode
                ? (fv.date ? dayjs(fv.date, "MM/DD/YYYY") : null)
                : (row.date ? dayjs(row.date, "MM/DD/YYYY") : null)
            }
            format="MM/DD/YYYY"
            style={{ ...inp, background: isFindMode ? "#FF8C00" : "#FFFF66", width: "100%", color: "#003bb3" }}
            size="small"
            onChange={(_, dateString) => {
              if (isFindMode) {
                onFindChange?.("date", String(dateString || ""));
              } else {
                onChange({ date: dateString });
              }
            }}
          />
        </div>
        <button type="button" onClick={goBackToQuotes}
          style={{ ...smBtn, fontWeight: 600, padding: "0 8px", flexShrink: 0 }}>
          Go Back To Quotes &gt;&gt;
        </button>
      </div>

      {/* ── BODY: flex row, explicit widths matching reference ── */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 2 }}>

        {/* Comment block — top row: Comment label + Target Sell label + Target Sell input;
            bottom row: wide Comment textarea spanning full width */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ ...lbl, marginBottom: 0 }}>Comment:</span>
            <span style={{ flex: 1 }} />
            <span style={{ ...lbl, marginBottom: 0 }}>Target Sell</span>
            <Input style={{ ...inp, width: 96, background: "#ffffff", border: "1px solid #000", color: "#000" }} size="small" />
          </div>
          {isFindMode ? (
            <Input.TextArea value={fv.comments || ""} onChange={(e) => onFindChange?.("comments", e.target.value)}
              style={{ fontSize: 11, height: 56, resize: "none", borderRadius: 0, width: "100%", background: "#c5c5c5", border: "1px solid #000", color: "#000", padding: "6px 8px" }} rows={3} />
          ) : (
            <Input.TextArea value={row.comments} onChange={(e) => onChange({ comments: e.target.value })}
              style={{ fontSize: 11, height: 56, resize: "none", borderRadius: 0, width: "100%", background: "#c5c5c5", border: "1px solid #000", color: "#000", padding: "6px 8px" }} rows={3} />
          )}
        </div>

        {/* Sales Comments */}
        <div style={{ width: 210, flexShrink: 0 }}>
          <span style={lbl}>Sales Comments</span>
          <Input.TextArea style={{ fontSize: 11, height: 56, resize: "none", borderRadius: 0, width: "100%", padding: "6px 8px" }} rows={3} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <span style={{ ...lbl, marginBottom: 0 }}>ID:</span>
            <Input
              value={row.id}
              readOnly
              style={{ ...inp, width: 90, background: "#c5c5c5", border: "1px solid #7f7f7f", color: "#000", fontWeight: 700 }}
              size="small"
            />
          </div>
        </div>

        {/* Status + Hot */}
        <div style={{ width: 128, flexShrink: 0 }}>
          <span style={lbl}>Status</span>
          <Select
            value={row.status || undefined}
            onChange={(v) => {
              if (v === "__edit_response_status__") {
                onOpenMainStatusModal();
                return;
              }
              onChange({ status: String(v || "") });
            }}
            style={{ width: "100%", fontSize: 11 }}
            size="small"
            allowClear
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 160, fontSize: 11 }}
            options={[
              ...statusOptions,
              { value: "__edit_response_status__", label: "✎ Edit Status..." },
            ]}
            placeholder=""
          />
          <span style={{ ...lbl, marginTop: 4 }}>Hot:</span>
          <Input
            value={hotText}
            onChange={(e) => setHotText(e.target.value)}
            onBlur={() => {
              const isHot = hotText.trim().toLowerCase() === "yes";
              onChange({ hot: isHot });
              setHotText(isHot ? "Yes" : "");
            }}
            onPressEnter={() => {
              const isHot = hotText.trim().toLowerCase() === "yes";
              onChange({ hot: isHot });
              setHotText(isHot ? "Yes" : "");
            }}
            style={{ height: 24, fontSize: 11, fontWeight: 700, borderRadius: 0, padding: "0 8px",
              background: "#ff1f1f", color: "#ffffff", border: "1px solid #a29f9f", textAlign: "center" }}
            size="small"
          />
        </div>

        {/* Added to WTS? / Added to WTB? — each row has its own border */}
        <div style={{ width: 150, flexShrink: 0 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={lbl}>Added to WTS?</span>
            <div style={{ display: "inline-flex", gap: 10, fontSize: 11, minHeight: 22, alignItems: "center", border: "1px solid #6f8f95", padding: "4px 8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <input type="radio" name={`wts-${row.localKey}`} checked={!!row.addedToWTS} onChange={() => onChange({ addedToWTS: true })} />Yes
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <input type="radio" name={`wts-${row.localKey}`} checked={!row.addedToWTS} onChange={() => onChange({ addedToWTS: false })} />No
              </label>
            </div>
          </div>
          <div>
            <span style={lbl}>Added to WTB?</span>
            <div style={{ display: "inline-flex", gap: 10, fontSize: 11, minHeight: 22, alignItems: "center", border: "1px solid #6f8f95", padding: "4px 8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <input type="radio" name={`wtb-${row.localKey}`} checked={!!row.addedToWTB} onChange={() => onChange({ addedToWTB: true })} />Yes
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <input type="radio" name={`wtb-${row.localKey}`} checked={!row.addedToWTB} onChange={() => onChange({ addedToWTB: false })} />No
              </label>
            </div>
          </div>
        </div>

        {/* Date Out × 2 */}
        <div style={{ width: 110, flexShrink: 0 }}>
          <span style={lbl}>Date Out</span>
          <Input style={{ ...inp, height: 24, padding: "0 8px" }} size="small" />
          <span style={{ ...lbl, marginTop: 4 }}>Date Out</span>
          <Input style={{ ...inp, height: 24, padding: "0 8px" }} size="small" />
        </div>

        {/* ID + Find Email */}
        <div style={{ width: 130, flexShrink: 0 }}>
          <span style={lbl}>ID:</span>
          <Input value={row.id} onChange={(e) => onIdChange(e.target.value)}
            onBlur={() => onIdLookup(row.id)} onPressEnter={() => onIdLookup(row.id)}
            style={{ ...inp, background: "#8B0000", color: "white", fontWeight: 700, textAlign: "center" }} size="small" />
          <span style={{ ...lbl, visibility: "hidden" }}>.</span>
          <button type="button" onClick={handleFindEmail} style={{ ...smBtn, width: "100%" }}>Find Email</button>
        </div>

        {/* Temporary Comment + Seller Type */}
        <div style={{ width: 170, flexShrink: 0 }}>
          <span style={lbl}>Temporary Comment</span>
          <Input.TextArea style={{ fontSize: 11, height: 56, resize: "none", borderRadius: 0, width: "100%", padding: "6px 8px" }} rows={3} />
          <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
            <span>Seller Type:</span>
            <span style={{ color: "red" }}>+</span>
          </div>
        </div>
      </div>

      {/* ── Email row — single nowrap line, no duplicates ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700 }}>Email:</span>
          {isFindMode ? (
            <div style={{ position: "relative" }}>
              <Input value={fv.email || ""} onChange={(e) => onFindChange?.("email", e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onPerformFind?.(); } }} style={{ ...inp, width: 240 }} size="small" />
              <SearchOutlined style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: 11, pointerEvents: "none" }} />
            </div>
          ) : (
            <Input value={row.email} onChange={(e) => onChange({ email: e.target.value })} style={{ ...inp, width: 240 }} size="small" />
          )}
          <button type="button" onClick={handleFindEmail} style={miniBtn} disabled={isFindMode}>@</button>
        </div>
        <span style={{ width: 16 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <button type="button" style={smBtn} onClick={handleRss}>RSS</button>
          <Input
            value={row.rssNote || ""}
            onChange={(e) => onChange({ rssNote: e.target.value })}
            style={{ height: 24, width: 72, border: "1px solid #333", background: "#99ccff", color: "#000", fontSize: 11, fontWeight: 600, borderRadius: 0, padding: "0 8px", textAlign: "center" }}
            size="small"
          />
        </div>
        <span style={{ width: 12, flexShrink: 0 }} />
        <button type="button" onClick={onAddWts} style={{ ...smBtn, border: "1px solid #333" }}>+WTS</button>
      </div>

      {/* ── Price Rank ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>Price Rank:</span>
        <Input value={row.priceRank || ""} onChange={(e) => onChange({ priceRank: e.target.value })}
          style={{ ...inp, width: 60 }} size="small" />
      </div>

      {/* ── Office / Contact tabs ── */}
      <OfficeContactTabs row={row} onChange={onChange} tabRowRightContent={tabRightContent} />
    </div>
  );
};

// Content Components
const ResponsesContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <div className="mb-6 bg-white rounded-md border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-base font-medium text-gray-800">
          Responses Report
        </h3>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          className="hover:bg-gray-100"
          size="small"
        />
      </div>
      <div className="p-4 space-y-4">
        <p className="text-gray-600">
          Responses report shows customer responses for WTB and CAL requests.
        </p>
        <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
          <Button onClick={onClose} size="small">
            Close
          </Button>
          <Button type="primary" size="small">
            Generate Report
          </Button>
        </div>
      </div>
    </div>
  );
};

const QuotesResponses: React.FC = () => {
  const PAGE_SIZE = 1000;
  const [rows, setRows] = useState<ResponseRow[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [isManufacturerModalOpen, setIsManufacturerModalOpen] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState<{ id: number; name: string } | null>(null);
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [subStatusOptions, setSubStatusOptions] = useState<StatusOption[]>([]);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState({ index: -1, value: "" });
  const [newStatus, setNewStatus] = useState("");
  const [isMainStatusModalOpen, setIsMainStatusModalOpen] = useState(false);
  const [editingMainStatus, setEditingMainStatus] = useState({ index: -1, value: "" });
  const [newMainStatus, setNewMainStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [nextId, setNextId] = useState<number>(1);
  const [activeContent, setActiveContent] = useState<string | null>(null);
  const QR_STATE_KEY = "quotesResponsesState";
  const _qrSaved = (() => { try { return JSON.parse(localStorage.getItem(QR_STATE_KEY) || "null") || {}; } catch { return {}; } })();
  const [sortRules, setSortRules] = useState<SortRule[]>(() => normalizeSortRules(_qrSaved.sortRules, RESPONSES_SORT_KEYS));
  const qrInitialRef = useRef(true);
  const [currentPage, setCurrentPage] = useState<number>(_qrSaved.currentPage || 1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentRecordIndex, setCurrentRecordIndex] = useState<number>(_qrSaved.currentRecordIndex || 0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const buildSortParams = (rules = sortRules) => {
    const sort = sortRulesToQuery(rules, RESPONSES_SORT_KEYS);
    return sort ? { sort } : {};
  };
  const [isLoadingRecord, setIsLoadingRecord] = useState<boolean>(false);
  const [isEuWtsFilterActive, setIsEuWtsFilterActive] =
    useState<boolean>(_qrSaved.isEuWtsFilterActive || false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rowsRef = useRef<ResponseRow[]>([]);
  const autosaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const navBeltRef = useRef<HTMLDivElement | null>(null);
  const navBeltDragging = useRef(false);
  const [navBeltDragIdx, setNavBeltDragIdx] = useState<number | null>(null);
  const modelParam = String(searchParams.get("model") || "").trim();
  const quoteIdParam = "";

  const parseId = (value: string | number | undefined) => {
    const parsed = parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const saveRow = async (localKey: string) => {
    const target = rowsRef.current.find((row) => row.localKey === localKey);
    if (!target) return;

    const payload = mapRowToPayload(target);
    if (!payload.listType) payload.listType = "WTB";

    try {
      const saved = target.qid
        ? await updateQuotesResponse(target.qid, payload)
        : await createQuotesResponse(payload);

      setRows((prev) =>
        prev.map((row) =>
          row.localKey === localKey
            ? {
                ...row,
                qid: saved.qid || row.qid,
                id: row.id || String(saved.id || ""),
              }
            : row,
        ),
      );

    } catch (error) {
      console.error("Failed to auto-save response", error);
    }
  };

  const queueAutoSave = (localKey: string) => {
    const previous = autosaveTimers.current[localKey];
    if (previous) clearTimeout(previous);
    autosaveTimers.current[localKey] = setTimeout(() => {
      saveRow(localKey);
    }, 700);
  };

  const updateRow = (
    localKey: string,
    patch: Partial<ResponseRow>,
    autoSave = true,
  ) => {
    setRows((prev) =>
      prev.map((r) => (r.localKey === localKey ? { ...r, ...patch } : r)),
    );
    if (autoSave) queueAutoSave(localKey);
  };

  const lookupById = async (localKey: string, id: string) => {
    const trimmed = String(id || "").trim();
    if (!trimmed) return;

    try {
      const data = await getQuotesResponsesByQuoteId(trimmed);
      if (!data.length) {
        message.warning(`No record found with ID: ${trimmed}`);
        return;
      }
      const mapped = mapApiToRow(data[0]);
      setRows((prev) =>
        prev.map((row) =>
          row.localKey === localKey
            ? { ...mapped, localKey, id: trimmed || mapped.id }
            : row,
        ),
      );

      if (rows.length > 0) {
        const idx = rows.findIndex((r) => r.localKey === localKey);
        if (idx >= 0) setCurrentRecordIndex(idx);
      }
    } catch (error) {
      message.error("Failed to fetch record by ID");
    }
  };

  const loadPage = async (page: number, preferredIndex = 0, rulesOverride = sortRules) => {
    setIsLoadingRecord(true);
    try {
      const response = await getQuotesResponses({ page, limit: PAGE_SIZE, ...buildSortParams(rulesOverride) });
      const list = Array.isArray(response?.data) ? response.data : [];
      const mapped = list.map(mapApiToRow);
      const total = Number(response?.pagination?.total || mapped.length);
      const pages = Math.max(1, Number(response?.pagination?.totalPages || 1));

      setRows(mapped);
      setTotalRecords(total);
      setTotalPages(pages);
      setCurrentPage(Math.min(Math.max(1, page), pages));
      const safeIndex = mapped.length
        ? Math.min(Math.max(0, preferredIndex), mapped.length - 1)
        : 0;
      setCurrentRecordIndex(safeIndex);
    } catch (error) {
      console.error("Failed to load responses page:", error);
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
      setCurrentPage(1);
      setCurrentRecordIndex(0);
    } finally {
      setIsLoadingRecord(false);
    }
  };

  const getGlobalRecordIndex = () => {
    if (rows.length === 0) return 0;
    if (isEuWtsFilterActive) {
      return Math.min(currentRecordIndex, Math.max(rows.length - 1, 0));
    }
    return (Math.max(currentPage, 1) - 1) * PAGE_SIZE + currentRecordIndex;
  };

  const jumpToGlobalRecord = async (globalIndex: number) => {
    if (isEuWtsFilterActive) {
      const safeIndex = Math.min(Math.max(0, globalIndex), Math.max(rows.length - 1, 0));
      setCurrentRecordIndex(safeIndex);
      return;
    }

    const safeIndex = Math.min(Math.max(0, globalIndex), Math.max(totalRecords - 1, 0));
    const targetPage = Math.floor(safeIndex / PAGE_SIZE) + 1;
    const targetIndex = safeIndex % PAGE_SIZE;

    if (targetPage === currentPage) {
      setCurrentRecordIndex(Math.min(targetIndex, Math.max(rows.length - 1, 0)));
      return;
    }

    await loadPage(targetPage, targetIndex);
  };

  const navigateToPrevious = async () => {
    const globalIndex = getGlobalRecordIndex();
    if (globalIndex > 0) {
      await jumpToGlobalRecord(globalIndex - 1);
    } else {
      message.warning("No previous record");
    }
  };

  const navigateToNext = async () => {
    const maxIndex = isEuWtsFilterActive ? rows.length - 1 : totalRecords - 1;
    const globalIndex = getGlobalRecordIndex();

    if (globalIndex < maxIndex) {
      await jumpToGlobalRecord(globalIndex + 1);
    } else {
      message.warning("No next record");
    }
  };

  const navigateToPreviousPage = async () => {
    if (isEuWtsFilterActive) {
      message.info("EU WTS filter is showing a fixed filtered list");
      return;
    }
    if (currentPage > 1) {
      await loadPage(currentPage - 1, 0);
    } else {
      message.warning("Already at first page");
    }
  };

  const loadManufacturers = async () => {
    try {
      const data = await getManufacturers();
      const list = Array.isArray(data) ? data : [];
      setManufacturers(list);
      const options = list.map((m: any) => ({
        value: m?.name,
        label: m?.name,
      }));
      setManufacturerOptions(options);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to load manufacturers",
      );
    }
  };

  const loadStatuses = async () => {
    try {
      const data = await getStatuses();
      if (Array.isArray(data) && data.length > 0) {
        const mappedStatuses = data
          .filter((item: any) => item.type === STATUS_TYPE_RESPONSE)
          .map((item: any) => ({
            id: item.id,
            value: item.statusName,
            label: item.statusName,
            type: item.type,
          }));
        const mappedSubStatuses = data
          .filter((item: any) => item.type === STATUS_TYPE_RESPONSE_SUB_STATUS)
          .map((item: any) => ({
            id: item.id,
            value: item.statusName,
            label: item.statusName,
            type: item.type,
          }));
        setStatusOptions(
          mappedStatuses.length > 0 ? mappedStatuses : SIMPLE_STATUS_OPTIONS,
        );
        setSubStatusOptions(mappedSubStatuses);
      } else {
        setStatusOptions(SIMPLE_STATUS_OPTIONS);
        setSubStatusOptions([]);
      }
    } catch (error) {
      console.error("Failed to load statuses", error);
    }
  };

  const handleEditStatus = (index: number) => {
    setEditingStatus({ index, value: subStatusOptions[index]?.label || "" });
  };

  const handleSaveStatus = async () => {
    if (editingStatus.index < 0 || !editingStatus.value.trim()) return;
    const targetStatus = subStatusOptions[editingStatus.index];
    if (!targetStatus?.id) return;

    try {
      await updateStatus(targetStatus.id, {
        statusName: editingStatus.value.trim(),
        type: targetStatus.type || STATUS_TYPE_RESPONSE_SUB_STATUS,
      });
      setEditingStatus({ index: -1, value: "" });
      await loadStatuses();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to update status",
      );
    }
  };

  const handleAddStatus = async () => {
    if (!newStatus.trim()) return;

    try {
      await createStatus({
        statusName: newStatus.trim(),
        type: STATUS_TYPE_RESPONSE_SUB_STATUS,
      });
      setNewStatus("");
      await loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to add status");
    }
  };

  const handleDeleteStatus = async (index: number) => {
    const targetStatus = subStatusOptions[index];
    if (!targetStatus?.id) return;

    try {
      await deleteStatus(targetStatus.id);
      await loadStatuses();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to delete status",
      );
    }
  };

  const handleEditMainStatus = (index: number) => {
    setEditingMainStatus({ index, value: statusOptions[index]?.label || "" });
  };

  const handleSaveMainStatus = async () => {
    if (editingMainStatus.index < 0 || !editingMainStatus.value.trim()) return;
    const targetStatus = statusOptions[editingMainStatus.index];
    if (!targetStatus?.id) return;

    try {
      await updateStatus(targetStatus.id, {
        statusName: editingMainStatus.value.trim(),
        type: targetStatus.type || STATUS_TYPE_RESPONSE,
      });
      setEditingMainStatus({ index: -1, value: "" });
      await loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  const handleAddMainStatus = async () => {
    if (!newMainStatus.trim()) return;

    try {
      await createStatus({
        statusName: newMainStatus.trim(),
        type: STATUS_TYPE_RESPONSE,
      });
      setNewMainStatus("");
      await loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to add status");
    }
  };

  const handleDeleteMainStatus = async (index: number) => {
    const targetStatus = statusOptions[index];
    if (!targetStatus?.id) return;

    try {
      await deleteStatus(targetStatus.id);
      await loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete status");
    }
  };

  const handleAddManufacturer = async () => {
    if (!newManufacturerName.trim()) { message.warning("Please enter a manufacturer name"); return; }
    try {
      await createManufacturer(newManufacturerName.trim());
      setNewManufacturerName("");
      await loadManufacturers();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to add manufacturer");
    }
  };

  const handleUpdateManufacturer = async () => {
    if (!editingManufacturer || !editingManufacturer.name.trim()) { message.warning("Please enter a manufacturer name"); return; }
    try {
      await updateManufacturer(editingManufacturer.id, editingManufacturer.name.trim());
      setEditingManufacturer(null);
      await loadManufacturers();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to update manufacturer");
    }
  };

  const handleDeleteManufacturer = async (id: number) => {
    try {
      await deleteManufacturer(id);
      await loadManufacturers();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete manufacturer");
    }
  };

  const navigateToNextPage = async () => {
    if (isEuWtsFilterActive) {
      message.info("EU WTS filter is showing a fixed filtered list");
      return;
    }
    if (currentPage < totalPages) {
      await loadPage(currentPage + 1, 0);
    } else {
      message.warning("Already at last page");
    }
  };

  const handleGoToRecord = async (recordNumber: number) => {
    const maxRecordNumber = isEuWtsFilterActive ? rows.length : totalRecords;
    if (recordNumber >= 1 && recordNumber <= maxRecordNumber) {
      await jumpToGlobalRecord(recordNumber - 1);
    }
  };

  const loadNextAvailableId = async () => {
    try {
      const nextAvailableId = await getQuotesResponsesNextId();
      setNextId(nextAvailableId);
      return nextAvailableId;
    } catch (error) {
      console.error("Failed to get next ID:", error);
      return 1;
    }
  };

  const createNewRecord = async () => {
    const newId = nextId;
    const newRow = createBlankRow(modelParam, quoteIdParam, String(newId));
    setRows((prev) => [newRow, ...prev]);
    setCurrentRecordIndex(0);

    // Save immediately to get the qid
    const payload = mapRowToPayload(newRow);
    if (!payload.listType) payload.listType = "WTB";

    try {
      const saved = await createQuotesResponse(payload);
      setRows((prev) =>
        prev.map((row) =>
          row.localKey === newRow.localKey
            ? {
                ...row,
                qid: saved.qid,
                id: String(saved.id || newId),
              }
            : row,
        ),
      );

      await loadNextAvailableId();
    } catch (error) {
      console.error("Failed to create new record", error);
      setRows([newRow]);
    }
  };

  const handleFindAll = async () => {
    setIsEuWtsFilterActive(false);
    setIsFindActive(false);
    setFindFields(emptyFind);
    findDateRef.current = "";
    setSortRules([]);
    await loadPage(1, 0, []);
  };

  const handleFindAllEuWts = async () => {
    setIsLoadingRecord(true);
    try {
      // Use backend listType filter (iLike %WTS%) to get all WTS variants (WTS, WTSQB, WTB/WTS, etc.)
      const response = await getQuotesResponses({ page: 1, limit: 10000, listType: "WTS", ...buildSortParams() });
      const list = Array.isArray(response?.data) ? response.data : [];
      if (!list.length) { message.info("No EU WTS records found"); return; }
      const mapped = list.map(mapApiToRow);
      setRows(mapped);
      setTotalRecords(mapped.length);
      setCurrentPage(1);
      setTotalPages(1);
      setCurrentRecordIndex(0);
      setIsEuWtsFilterActive(true);
    } catch (error) {
      console.error("Failed to filter EU WTS responses:", error);
      message.error("Failed to load EU WTS responses");
    } finally {
      setIsLoadingRecord(false);
    }
  };

  const handleScrollUp = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleFindAllHot = () => {
    message.info("Find All HOT is not connected yet.");
  };

  const handleBasecamp = () => {
    message.info("BASECAMP is not connected yet.");
  };

  const handleDeleteCurrentRecord = async () => {
    if (!currentRow) {
      message.warning("No response record selected to delete");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this quote response record?",
    );
    if (!confirmed) return;

    try {
      if (currentRow.qid) {
        await deleteQuotesResponse(currentRow.qid);
      } else {
        setRows((prev) => prev.filter((row) => row.localKey !== currentRow.localKey));
      }

      // message.success("Quote response record deleted successfully");

      if (isEuWtsFilterActive) {
        await handleFindAllEuWts();
      } else {
        await loadPage(1, 0);
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          "Failed to delete quote response record",
      );
    }
  };

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // ── Find mode — declared here so the save effect below can reference them ──
  const emptyFind = { company: "", manu: "", model: "", options: "", email: "", price: "", qty: "", comments: "", status: "", subStatus: "", responseType: "", date: "" };
  const [isFindMode, setIsFindMode] = useState(false);
  const [isFindActive, setIsFindActive] = useState(_qrSaved.isFindActive || false);
  const [findFields, setFindFields] = useState<typeof emptyFind>(_qrSaved.findFields || emptyFind);
  const findDateRef = useRef(_qrSaved.findFields?.date || "");

  useEffect(() => {
    if (!qrInitialRef.current) {
      const currentQid = rows[currentRecordIndex]?.qid ?? null;
      localStorage.setItem(QR_STATE_KEY, JSON.stringify({ currentPage, currentRecordIndex, currentQid, isEuWtsFilterActive, isFindActive, findFields, sortRules }));
    }
  }, [currentPage, currentRecordIndex, isEuWtsFilterActive, isFindActive, findFields, sortRules]);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await loadManufacturers();
        await loadStatuses();
        await loadNextAvailableId();
        const restoreByQid = (mapped: any[], fallbackIdx: number) => {
          const savedQid = _qrSaved.currentQid;
          if (savedQid != null) {
            const qidIdx = mapped.findIndex((r) => r.qid === savedQid || String(r.qid) === String(savedQid));
            return qidIdx >= 0 ? qidIdx : Math.min(fallbackIdx, mapped.length - 1);
          }
          return Math.min(fallbackIdx, mapped.length - 1);
        };

        if (_qrSaved.isEuWtsFilterActive) {
          // Re-apply EU/WTS filter using backend listType filter
          const response = await getQuotesResponses({ page: 1, limit: 10000, listType: "WTS", ...buildSortParams() });
          const list = Array.isArray(response?.data) ? response.data : [];
          if (list.length > 0) {
            const mapped = list.map(mapApiToRow);
            setRows(mapped);
            setTotalRecords(mapped.length);
            setCurrentPage(1);
            setTotalPages(1);
            setIsEuWtsFilterActive(true);
            setCurrentRecordIndex(restoreByQid(mapped, _qrSaved.currentRecordIndex || 0));
          } else {
            await loadPage(_qrSaved.currentPage || 1, _qrSaved.currentRecordIndex || 0);
          }
        } else if (_qrSaved.isFindActive) {
          // Re-apply saved find criteria
          const f = _qrSaved.findFields || {};
          const params: any = { page: 1, limit: 10000, ...buildSortParams() };
          if (f.company?.trim())      params.company      = f.company.trim();
          if (f.manu?.trim())         params.manu         = f.manu.trim();
          if (f.model?.trim())        params.model        = f.model.trim();
          if (f.options?.trim())      params.options      = f.options.trim();
          if (f.email?.trim())        params.email        = f.email.trim();
          if (f.price?.trim())        params.price        = f.price.trim();
          if (f.qty?.trim())          params.qty          = f.qty.trim();
          if (f.comments?.trim())     params.comments     = f.comments.trim();
          if (f.status?.trim())       params.dStatus      = f.status.trim();
          if (f.responseType?.trim()) params.listType     = f.responseType.trim();
          if (f.date?.trim())         params.date         = f.date.trim();
          const hasParams = Object.keys(params).length > 2;
          if (hasParams) {
            try {
              const res = await getQuotesResponses(params);
              const list = Array.isArray(res?.data) ? res.data : [];
              if (list.length > 0) {
                const mapped = list.map(mapApiToRow);
                setRows(mapped);
                setTotalRecords(mapped.length);
                setCurrentPage(1);
                setTotalPages(1);
                setIsFindActive(true);
                setCurrentRecordIndex(restoreByQid(mapped, _qrSaved.currentRecordIndex || 0));
              } else {
                await loadPage(_qrSaved.currentPage || 1, _qrSaved.currentRecordIndex || 0);
              }
            } catch {
              await loadPage(_qrSaved.currentPage || 1, _qrSaved.currentRecordIndex || 0);
            }
          } else {
            await loadPage(_qrSaved.currentPage || 1, _qrSaved.currentRecordIndex || 0);
          }
        } else {
          await loadPage(_qrSaved.currentPage || 1, _qrSaved.currentRecordIndex || 0);
        }
      } catch (error) {
        message.error("Failed to initialize page");
        const newRow = createBlankRow(modelParam, quoteIdParam, "1");
        setRows([newRow]);
      } finally {
        qrInitialRef.current = false;
        setLoading(false);
      }
    };

    initialize();
    return () => {
      Object.values(autosaveTimers.current).forEach(clearTimeout);
    };
  }, [modelParam]);

  const handleIdChange = (localKey: string, id: string) => {
    updateRow(localKey, { id }, false);
  };

  const handleCloseContent = () => {
    setActiveContent(null);
  };

  const renderContent = () => {
    switch (activeContent) {
      case "responses":
        return <ResponsesContent onClose={handleCloseContent} />;
      default:
        return null;
    }
  };

  // Calculate current record display number
  const currentRecordDisplay = rows.length > 0 ? getGlobalRecordIndex() + 1 : 0;
  const currentRow =
    rows.length > 0 ? rows[Math.min(currentRecordIndex, rows.length - 1)] : null;
  const [recordInputValue, setRecordInputValue] = useState("");

  const openFindMode = () => { findDateRef.current = ""; setFindFields(emptyFind); setIsFindMode(true); };
  const cancelFindMode = () => {
    findDateRef.current = "";
    setIsFindMode(false);
    setFindFields(emptyFind);
    setIsFindActive(false);
  };

  const handlePerformFind = async () => {
    const f = findFields;
    const dateVal = findDateRef.current.trim();
    const hasAny = Object.values(f).some((v) => String(v || "").trim()) || !!dateVal;
    if (!hasAny) { message.warning("Enter at least one search value"); return; }
    const params: any = { page: 1, limit: 10000, ...buildSortParams() };
    if (f.company.trim())      params.company      = f.company.trim();
    if (f.manu.trim())         params.manu         = f.manu.trim();
    if (f.model.trim())        params.model        = f.model.trim();
    if (f.options.trim())      params.options      = f.options.trim();
    if (f.email.trim())        params.email        = f.email.trim();
    if (f.price.trim())        params.price        = f.price.trim();
    if (f.qty.trim())          params.qty          = f.qty.trim();
    if (f.comments.trim())     params.comments     = f.comments.trim();
    if (f.status.trim())       params.dStatus      = f.status.trim();
    if (f.responseType.trim()) params.listType     = f.responseType.trim();
    if (dateVal)               params.date         = dateVal;
    try {
      const res = await getQuotesResponses(params);
      const list = Array.isArray(res?.data) ? res.data : [];
      if (!list.length) { message.info("No records found"); setIsFindMode(false); return; }
      const mapped = list.map(mapApiToRow);
      setRows(mapped);
      setTotalRecords(Number(res?.pagination?.total || list.length));
      setCurrentPage(1);
      setTotalPages(Math.max(1, Number(res?.pagination?.totalPages || 1)));
      setCurrentRecordIndex(0);
      setIsEuWtsFilterActive(false);
      setIsFindActive(true);
    } catch { message.error("Search failed"); }
    setIsFindMode(false);
  };

  useEffect(() => {
    setRecordInputValue(rows.length > 0 ? String(currentRecordDisplay) : "");
  }, [currentRecordDisplay, rows.length]);

  const handleApplySortRules = async (rules: SortRule[]) => {
    const normalized = normalizeSortRules(rules, RESPONSES_SORT_KEYS);
    setSortRules(normalized);
    setIsEuWtsFilterActive(false);
    setIsFindActive(false);
    setCurrentRecordIndex(0);
    await loadPage(1, 0, normalized);
  };
  const handleRecordInputCommit = async (value: string) => {
    const parsed = Number.parseInt(value, 10);
    const maxRecordNumber = isEuWtsFilterActive ? rows.length : totalRecords;

    if (!Number.isFinite(parsed) || parsed < 1 || parsed > maxRecordNumber) {
      setRecordInputValue(rows.length > 0 ? String(currentRecordDisplay) : "");
      return;
    }
    await jumpToGlobalRecord(parsed - 1);
  };

  if (loading && rows.length === 0 && totalRecords > 0) {
    return (
      <div className="p-2 bg-slate-50 min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="quotes-responses-page bg-slate-50 min-h-screen overflow-x-hidden">
      <CommonHeader
        className="mb-0"
        currentRecord={recordInputValue}
        disableNext={
          rows.length === 0 ||
          getGlobalRecordIndex() >=
            (isEuWtsFilterActive ? rows.length - 1 : totalRecords - 1)
        }
        disablePrevious={rows.length === 0 || getGlobalRecordIndex() <= 0}
        isFindMode={isFindMode}
        onCancelFind={cancelFindMode}
        onDeleteRecord={handleDeleteCurrentRecord}
        onFind={openFindMode}
        onNewRecord={createNewRecord}
        onNextRecord={navigateToNext}
        onPerformFind={handlePerformFind}
        onPreviousRecord={navigateToPrevious}
        onRecordChange={setRecordInputValue}
        onRecordCommit={handleRecordInputCommit}
        onRecordSliderCommit={jumpToGlobalRecord}
        onShowAll={handleFindAll}
        recordCount={isEuWtsFilterActive ? rows.length : totalRecords}
        recordIndex={
          rows.length > 0
            ? Math.min(
                getGlobalRecordIndex(),
                Math.max((isEuWtsFilterActive ? rows.length : totalRecords) - 1, 0),
              )
            : 0
        }
        rightContent={<PageLogoutButton className="quotes-responses-logout-btn" />}
        totalActiveCount={rows.length}
        totalLabel={isEuWtsFilterActive ? "Filtered (EU WTS)" : (sortRules.length > 0 ? "Total (Sorted)" : "Total (Unsorted)")}
        totalRecordCount={totalRecords}
        totalValue={isEuWtsFilterActive ? `${rows.length} / ${totalRecords}` : totalRecords}
        sortColumns={RESPONSES_EXPORT_COLUMNS}
        sortTitle="Sort Quotes Responses Records"
        sortRules={sortRules}
        onApplySort={handleApplySortRules}
        exportColumns={RESPONSES_EXPORT_COLUMNS}
        getData={() => rows}
        onImport={async (data) => { await bulkImportQuoteResponses(data); }}
        exportFilename="quote-responses"
      />
      <div className="mb-4">
        <div className="overflow-x-auto border border-gray-400 bg-[#e6f6f8] px-[6px] py-[4px]">
          <div className="grid min-w-[1180px] grid-cols-[150px_170px_120px_430px] items-start gap-x-6">
            <div className="flex flex-col pl-7 pt-1">
              <h1 className="text-[18px] font-bold leading-[1.05] text-black">
                WTB &amp; Cal
                <br />
                Responses
              </h1>
            </div>

            <div className="flex flex-col items-start gap-2 pt-6">
              <div className="flex items-center gap-0">
                <button
                  className="h-[24px] min-w-[66px] border border-black bg-[#c7c7c7] px-3 text-[12px] text-black"
                  onClick={() => navigate("/Quotes")}
                >
                  Quote
                </button>
                <button className="h-[24px] min-w-[68px] border border-black bg-[#84f0f0] px-3 text-[12px] text-black">
                  Equipment
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <div className="flex items-center gap-[6px]">
                <span className="w-[30px] text-[12px] font-bold text-black">WTB:</span>
                <button className="h-[22px] min-w-[42px] border border-black bg-[#c7c7c7] px-3 text-[12px] leading-none text-black">
                  All
                </button>
              </div>

              <div className="flex items-center gap-[6px]">
                <span className="w-[30px] text-[12px] font-bold text-black">CAL:</span>
                <button className="h-[22px] min-w-[42px] border border-black bg-[#c7c7c7] px-3 text-[12px] leading-none text-black">
                  All
                </button>
              </div>
            </div>

            <div className="ml-20 flex flex-col gap-[4px] justify-self-start pt-1">
              <div className="flex items-center gap-[6px]">
                <Button
                  size="small"
                  className="!h-[24px] !min-w-[64px] !rounded-none !border !border-black px-3 !text-[12px]"
                  onClick={() => setShowSearch(true)}
                >
                  FIND
                </Button>
                <Button
                  size="small"
                  className="!h-[24px] !min-w-[64px] !rounded-none !border !border-black px-3 !text-[12px]"
                  onClick={createNewRecord}
                >
                  New
                </Button>
                <Button
                  size="small"
                  className="!h-[24px] !min-w-[98px] !rounded-none !border !border-black px-3 !text-[12px]"
                  onClick={handleFindAllEuWts}
                >
                  Find All EU WTS
                </Button>
                <Button
                  size="small"
                  className="!h-[24px] !min-w-[98px] !rounded-none !border !border-black px-3 !text-[12px]"
                  onClick={handleScrollUp}
                >
                  Scroll Up
                </Button>
              </div>
              <div className="flex items-center gap-[6px] pl-[2px]">
                <Button
                  size="small"
                  className="!h-[24px] !min-w-[76px] !rounded-none !border !border-black px-3 !text-[12px]"
                  onClick={handleFindAll}
                >
                  FIND ALL
                </Button>
                <Button
                  size="small"
                  className="!h-[24px] !min-w-[96px] !rounded-none !border !border-black px-3 !text-[12px]"
                  onClick={handleFindAllHot}
                >
                  Find All HOT!
                </Button>
                <Button
                  size="small"
                  className="!h-[24px] !min-w-[92px] !rounded-none !border !border-black px-3 !text-[12px]"
                  onClick={handleBasecamp}
                >
                  BASECAMP
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderContent()}

      <div className="bg-white rounded-2xl shadow-sm p-4 qr-main-panel">
        {isLoadingRecord && (
          <div className="text-sm text-gray-600 px-2 pb-3">
            Loading record...
          </div>
        )}
        {!isLoadingRecord && currentRow && (
          <ResponseCard
            key={currentRow.localKey}
            row={currentRow}
            manufacturerOptions={manufacturerOptions}
            statusOptions={statusOptions}
            subStatusOptions={subStatusOptions}
            onOpenStatusModal={() => setIsStatusModalOpen(true)}
            onOpenMainStatusModal={() => setIsMainStatusModalOpen(true)}
            onOpenManufacturerModal={() => setIsManufacturerModalOpen(true)}
            isFindMode={isFindMode}
            findFields={findFields}
            onFindChange={(field, value) => {
              if (field === "date") findDateRef.current = String(value || "");
              setFindFields((p) => ({ ...p, [field]: value }));
            }}
            onPerformFind={handlePerformFind}
            onChange={(patch) => updateRow(currentRow.localKey, patch)}
            onIdChange={(id) => handleIdChange(currentRow.localKey, id)}
            onIdLookup={(id) => lookupById(currentRow.localKey, id)}
            onAddWts={async () => {
              try {
                const payload: any = {
                  eqBrand: currentRow.manu || undefined,
                  eqModel: currentRow.model || undefined,
                  eqOptions: currentRow.options || undefined,
                  listType: "WTS",
                  type: "WTS",
                  recordId: currentRow.officeId || undefined,
                  sellerType: currentRow.dealer || "Dealer",
                  status: "Active",
                  source: currentRow.company || undefined,
                  createdDate: currentRow.date || undefined,
                };
                const saved = await createQuoteWtb(payload);
              } catch (error) {
                console.error("Failed to create WTS record", error);
                message.error("Failed to create WTS record");
              }
            }}
          />
        )}
      </div>

      {isStatusModalOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => {
              setIsStatusModalOpen(false);
              setEditingStatus({ index: -1, value: "" });
              setNewStatus("");
            }}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
            <div className="relative w-80 max-h-96 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-200 p-3">
                <h3 className="text-[12px] font-semibold text-gray-700">
                  Edit Sub Status Options
                </h3>
                <Button
                  type="text"
                  size="small"
                  className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setIsStatusModalOpen(false);
                    setEditingStatus({ index: -1, value: "" });
                    setNewStatus("");
                  }}
                >
                  x
                </Button>
              </div>

              <div className="max-h-64 overflow-y-auto p-3">
                <div className="mb-3 space-y-2">
                  {subStatusOptions.map((option, index) => (
                    <div
                      key={option.id ?? `${option.value}-${index}`}
                      className="flex items-center gap-1 group"
                    >
                      {editingStatus.index === index ? (
                        <>
                          <Input
                            value={editingStatus.value}
                            onChange={(e) =>
                              setEditingStatus((prev) => ({
                                ...prev,
                                value: e.target.value,
                              }))
                            }
                            size="small"
                            className="h-6 flex-1 text-[10px]"
                            autoFocus
                            onPressEnter={handleSaveStatus}
                          />
                          <Button
                            type="primary"
                            size="small"
                            className="h-5 w-5 p-0"
                            onClick={handleSaveStatus}
                          >
                            ✓
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            className="h-5 w-5 p-0 text-gray-500"
                            onClick={() =>
                              setEditingStatus({ index: -1, value: "" })
                            }
                          >
                            x
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-[10px]">
                            {option.label}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 text-gray-400 hover:text-blue-600"
                              onClick={() => handleEditStatus(index)}
                            >
                              <EditOutlined className="text-[12px]" />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 text-gray-400 hover:text-red-600"
                              onClick={() => handleDeleteStatus(index)}
                              disabled={!option.id}
                            >
                              <DeleteOutlined className="text-[12px]" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-2">
                  <div className="mb-1 text-[10px] font-medium text-gray-500">
                    Add New Sub Status
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      placeholder="Enter new status"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      size="small"
                      className="h-6 flex-1 text-[10px]"
                      onPressEnter={handleAddStatus}
                    />
                    <Button
                      type="primary"
                      size="small"
                      className="h-6 px-2 text-[9px] bg-[#004aad]"
                      onClick={handleAddStatus}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isMainStatusModalOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => {
              setIsMainStatusModalOpen(false);
              setEditingMainStatus({ index: -1, value: "" });
              setNewMainStatus("");
            }}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
            <div className="relative w-80 max-h-96 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-200 p-3">
                <h3 className="text-[12px] font-semibold text-gray-700">
                  Edit Status Options
                </h3>
                <Button
                  type="text"
                  size="small"
                  className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setIsMainStatusModalOpen(false);
                    setEditingMainStatus({ index: -1, value: "" });
                    setNewMainStatus("");
                  }}
                >
                  x
                </Button>
              </div>

              <div className="max-h-64 overflow-y-auto p-3">
                <div className="mb-3 space-y-2">
                  {statusOptions.map((option, index) => (
                    <div
                      key={option.id ?? `${option.value}-${index}`}
                      className="flex items-center gap-1 group"
                    >
                      {editingMainStatus.index === index ? (
                        <>
                          <Input
                            value={editingMainStatus.value}
                            onChange={(e) =>
                              setEditingMainStatus((prev) => ({
                                ...prev,
                                value: e.target.value,
                              }))
                            }
                            size="small"
                            className="h-6 flex-1 text-[10px]"
                            autoFocus
                            onPressEnter={handleSaveMainStatus}
                          />
                          <Button
                            type="primary"
                            size="small"
                            className="h-5 w-5 p-0"
                            onClick={handleSaveMainStatus}
                          >
                            ✓
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            className="h-5 w-5 p-0 text-gray-500"
                            onClick={() =>
                              setEditingMainStatus({ index: -1, value: "" })
                            }
                          >
                            x
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-[10px]">
                            {option.label}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 text-gray-400 hover:text-blue-600"
                              onClick={() => handleEditMainStatus(index)}
                            >
                              <EditOutlined className="text-[12px]" />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 text-gray-400 hover:text-red-600"
                              onClick={() => handleDeleteMainStatus(index)}
                              disabled={!option.id}
                            >
                              <DeleteOutlined className="text-[12px]" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-2">
                  <div className="mb-1 text-[10px] font-medium text-gray-500">
                    Add New Status
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      placeholder="Enter new status"
                      value={newMainStatus}
                      onChange={(e) => setNewMainStatus(e.target.value)}
                      size="small"
                      className="h-6 flex-1 text-[10px]"
                      onPressEnter={handleAddMainStatus}
                    />
                    <Button
                      type="primary"
                      size="small"
                      className="h-6 px-2 text-[9px] bg-[#004aad]"
                      onClick={handleAddMainStatus}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isManufacturerModalOpen && (
        <>
          <div
            className="fixed inset-0 z-40 backdrop-blur-sm bg-white/30"
            onClick={() => {
              setIsManufacturerModalOpen(false);
              setEditingManufacturer(null);
              setNewManufacturerName("");
            }}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
            <div className="relative bg-white rounded-lg shadow-xl border border-gray-300 w-96 max-h-[500px] overflow-hidden">
              <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <h3 className="text-[14px] font-semibold text-gray-700">Manage Manufacturers</h3>
                <Button
                  type="text"
                  size="small"
                  className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setIsManufacturerModalOpen(false);
                    setEditingManufacturer(null);
                    setNewManufacturerName("");
                  }}
                >
                  ✕
                </Button>
              </div>

              <div className="p-3 max-h-[300px] overflow-y-auto">
                <div className="space-y-2">
                  <div className="text-[11px] font-medium text-gray-500 mb-2">
                    Existing Manufacturers ({manufacturers.length})
                  </div>
                  {manufacturers.map((manufacturer) => (
                    <div
                      key={manufacturer.id}
                      className="flex items-center gap-1 group bg-gray-50 rounded p-1.5"
                    >
                      {editingManufacturer?.id === manufacturer.id ? (
                        <>
                          <Input
                            value={editingManufacturer.name}
                            onChange={(e) =>
                              setEditingManufacturer({ ...editingManufacturer, name: e.target.value })
                            }
                            size="small"
                            className="h-7 text-[11px] flex-1"
                            autoFocus
                            onPressEnter={handleUpdateManufacturer}
                          />
                          <Button
                            type="primary"
                            size="small"
                            className="h-6 w-6 p-0 flex items-center justify-center bg-green-600 hover:bg-green-700"
                            onClick={handleUpdateManufacturer}
                          >
                            <span className="text-[11px]">✓</span>
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            className="h-6 w-6 p-0 flex items-center justify-center text-gray-500"
                            onClick={() => setEditingManufacturer(null)}
                          >
                            <span className="text-[11px]">✕</span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="text-[11px] flex-1">{manufacturer.name}</span>
                          <div className="flex gap-1">
                            <Button
                              type="text"
                              size="small"
                              className="h-5 w-5 p-0 flex items-center justify-center text-gray-400 hover:text-blue-600"
                              onClick={() => setEditingManufacturer({ id: manufacturer.id, name: manufacturer.name })}
                            >
                              <EditOutlined className="text-[12px]" />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              className="h-5 w-5 p-0 flex items-center justify-center text-gray-400 hover:text-red-600"
                              onClick={() => handleDeleteManufacturer(manufacturer.id)}
                            >
                              <DeleteOutlined className="text-[12px]" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {manufacturers.length === 0 && (
                    <div className="text-center text-gray-400 text-[11px] py-4">
                      No manufacturers added yet
                    </div>
                  )}
                </div>
              </div>

              <div className="px-3 py-2 border-t border-gray-200 bg-white">
                <div className="text-[11px] font-medium text-gray-500 mb-2">Add New Manufacturer</div>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Enter manufacturer name"
                    value={newManufacturerName}
                    onChange={(e) => setNewManufacturerName(e.target.value)}
                    size="small"
                    className="h-7 text-[11px] flex-1"
                    onPressEnter={handleAddManufacturer}
                  />
                  <Button
                    type="default"
                    size="small"
                    className="h-7 px-3 text-[11px] bg-[#C0C0C0] hover:!bg-[#b0b0b0] text-black hover:!text-black border border-[#808080] hover:!border-[#808080]"
                    onClick={handleAddManufacturer}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="p-2 border-t border-gray-200 flex justify-end bg-gray-50">
                <Button
                  size="small"
                  className="h-7 px-3 text-[11px]"
                  onClick={() => {
                    setIsManufacturerModalOpen(false);
                    setEditingManufacturer(null);
                    setNewManufacturerName("");
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuotesResponses;


